import { useState, useRef, useEffect } from 'react';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonButtons,
  IonButton,
  IonMenuButton,
  IonTitle,
  IonContent,
  IonList,
  IonItem,
  IonSelect,
  IonSelectOption,
  IonIcon,
  IonDatetime,
  IonModal,
  IonRefresher,
  IonRefresherContent,
  useIonViewWillEnter
} from '@ionic/react';
import { calendarOutline } from 'ionicons/icons';
import { observer } from 'mobx-react-lite';
import moment from 'moment';

import WorkoutAssignmentListItemLoader from './components/WorkoutAssignmentListItem/Loader';
import WorkoutSingle from './components/WorkoutSingle';
import TwoOFour from 'components/TwoOFour';

import workoutAssignmentStore from 'stores/workout-assignment.store';
import apolloClient from 'libs/apollo/apollo.lib';

import classes from './style.module.scss';

const WORKOUT_ASSIGNMENT = `
  id
  start_date
  end_date
  notes
  workoutAssignmentCategory {
    id
    name
  }
  workout {
    id
    name
    description
    cover
    workoutTotalDays
  }
`;

const WorkoutsPage = ({ match, history }) => {
  const [selectedCategory, setSelectedCateogry] = useState(-1);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const datePicker = useRef();
  const selectedDate = workoutAssignmentStore.getSelectedDate();
  const workoutAssignments = workoutAssignmentStore.getObjects();

  useIonViewWillEnter(() => {
    if (!workoutAssignmentStore.getSelectedDate()) {
      workoutAssignmentStore.setIsLoading(true);
      workoutAssignmentStore.setSelectedDate(moment());
    } else {
      fetchWorkoutAssignments();
    }
  }, []);

  useEffect(() => {
    if (selectedDate) {
      const searchDate = selectedDate.format('YYYY-MM-DD');
      const query = {
        AND: [
          {
            column: 'start_date',
            operator: 'LTE',
            value: searchDate
          },
          {
            column: 'end_date',
            operator: 'GTE',
            value: searchDate
          }
        ]
      };
      workoutAssignmentStore.setFields(WORKOUT_ASSIGNMENT);
      workoutAssignmentStore.setQuery(query);
      fetchWorkoutAssignments();
    }
  }, [selectedDate]);

  const availableCategories = workoutAssignments.map(
    (workoutAssignment) => workoutAssignment.workoutAssignmentCategory
  );
  const uniqueCategories = [];

  availableCategories.forEach((availableCategory) => {
    const foundIndex = uniqueCategories.findIndex(
      (category) => category.id === availableCategory.id
    );
    if (foundIndex === -1) {
      uniqueCategories.push(availableCategory);
    }
  });

  const fetchWorkoutAssignments = async () => {
    try {
      await workoutAssignmentStore.searchObjectsAll();
    } catch (error) {
      console.log(error);
    }
  };

  const _renderLoader = () => {
    return (
      <IonList>
        {Array.from({ length: 4 }).map((_, index) => (
          <IonItem
            detail={false}
            lines='none'
            type='button'
            button
            key={`workoutAssignmentLoader_${index}`}
          >
            <div className={classes.workoutMainContainer}>
              <WorkoutAssignmentListItemLoader />
            </div>
          </IonItem>
        ))}
      </IonList>
    );
  };

  const _renderNoContent = () => {
    return <TwoOFour message='No workouts assigned!' />;
  };

  const _renderContent = () => {
    const isLoading = workoutAssignmentStore.getIsLoading();
    const noContent = workoutAssignments.length === 0;

    if (isLoading && !isRefreshing) {
      return _renderLoader();
    }

    if (noContent) {
      return _renderNoContent();
    }

    const getWorkoutAssignmentUrl = (workoutAssignment) => {
      const current_url = match.url;
      const workoutAssignmentId = workoutAssignment.id;
      const workoutName = workoutAssignment.workout.name;
      return `${current_url}/${workoutAssignmentId}?name=${workoutName}`;
    };

    return (
      <>
        <IonRefresher
          onIonRefresh={async (e) => {
            setIsRefreshing(true);
            await apolloClient.clearStore();
            await fetchWorkoutAssignments();
            e.detail.complete();
            setIsRefreshing(false);
          }}
          slot='fixed'
        >
          <IonRefresherContent></IonRefresherContent>
        </IonRefresher>
        <div className={classes.contentTitle}>
          <IonSelect
            value={selectedCategory}
            onIonChange={(e) => setSelectedCateogry(e.detail.value)}
            interface='action-sheet'
          >
            <IonSelectOption value={-1}>All</IonSelectOption>
            {uniqueCategories?.map((category) => (
              <IonSelectOption value={category.id} key={category.id}>
                {category.name}
              </IonSelectOption>
            ))}
          </IonSelect>
        </div>
        <IonList>
          {workoutAssignments.map((workoutAssignment, index) => (
            <IonItem
              detail={false}
              lines='none'
              type='button'
              button
              key={`${workoutAssignment.id}_${index}`}
              onClick={() => {
                history.push(getWorkoutAssignmentUrl(workoutAssignment));
              }}
              style={{
                display:
                  workoutAssignment.workoutAssignmentCategory.id ===
                    selectedCategory || selectedCategory === -1
                    ? 'block'
                    : 'none'
              }}
              className={classes.workoutItemContainer}
            >
              <div className={classes.workoutMainContainer}>
                <WorkoutSingle
                  workoutAssignmentId={workoutAssignment.id}
                  key={workoutAssignment.id}
                  summary
                />
              </div>
            </IonItem>
          ))}
        </IonList>
      </>
    );
  };

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot='start'>
            <IonMenuButton></IonMenuButton>
          </IonButtons>
          <IonTitle>Workouts</IonTitle>
          <IonButtons slot='end'>
            <IonButton id='open-date-picker-workout'>
              <IonIcon icon={calendarOutline} />
            </IonButton>
          </IonButtons>
          <IonModal
            ref={datePicker}
            trigger='open-date-picker-workout' 
          >
            <IonHeader>
              <IonToolbar>
                <IonTitle>Select Date</IonTitle>
                <IonButtons slot='end'>
                  <IonButton onClick={() => datePicker.current?.dismiss()}>
                    Close
                  </IonButton>
                </IonButtons>
              </IonToolbar>
            </IonHeader>
            <IonContent>
              <IonDatetime
                presentation='date' 
                value={selectedDate?.format('YYYY-MM-DD')}
                size='cover'
                onIonChange={(e) => {
                  if (e.detail.value) {
                    workoutAssignmentStore.setSelectedDate(
                      moment(e.detail.value, 'YYYY-MM-DD')
                    );
                  }
                }}
              ></IonDatetime>
            </IonContent>
          </IonModal>
        </IonToolbar>
      </IonHeader>
      <IonContent className={classes.ionContent}>{_renderContent()}</IonContent>
    </IonPage>
  );
};

export default observer(WorkoutsPage);
